import type { Organization, SlotTemplate } from "@/api/types";

// French national holidays + Easter-derived ones for 2024-2030. Keep in sync
// with the backend (cortex_api.routers.organizations._FR_HOLIDAYS).
const FR_HOLIDAYS: ReadonlySet<string> = new Set([
  // Fixed
  ...range(2024, 2030).flatMap((y) => [
    `${y}-01-01`,
    `${y}-05-01`,
    `${y}-05-08`,
    `${y}-07-14`,
    `${y}-08-15`,
    `${y}-11-01`,
    `${y}-11-11`,
    `${y}-12-25`,
  ]),
  // Easter-based
  "2024-04-01", "2024-05-09", "2024-05-20",
  "2025-04-21", "2025-05-29", "2025-06-09",
  "2026-04-06", "2026-05-14", "2026-05-25",
  "2027-03-29", "2027-05-06", "2027-05-17",
]);

function range(a: number, b: number): number[] {
  const out: number[] = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}


export function isWeekend(iso: string): boolean {
  const d = new Date(iso);
  const w = d.getDay(); // 0 = Sunday, 6 = Saturday
  return w === 0 || w === 6;
}

export function isFrenchHoliday(iso: string): boolean {
  return FR_HOLIDAYS.has(iso);
}

function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The working day wedged against a public holiday: the Monday before a Tuesday
 * holiday, the Friday after a Thursday one. Mirrors `calendar_utils.is_bridge_day`.
 *
 * A Monday *after* a Sunday holiday bridges nothing — the weekend is already
 * on that side — hence only these two cases.
 */
export function isBridgeDay(iso: string): boolean {
  const w = new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  if (w === 1) return FR_HOLIDAYS.has(shiftIso(iso, 1));
  if (w === 5) return FR_HOLIDAYS.has(shiftIso(iso, -1));
  return false;
}

/**
 * Return true when the organization is supposed to be operating on this
 * (date, slot) combination — i.e., the slot's time window falls inside the
 * org's operating hours and the day isn't excluded by weekend/holiday rules.
 *
 * Conservatively returns true when the org has no operating-hours config
 * (round-the-clock).
 */
export function slotIsOperating(
  org: Organization | null,
  iso: string,
  slot: Pick<SlotTemplate, "start_time" | "end_time">,
): boolean {
  if (!org) return true;
  if (!org.operates_weekends && isWeekend(iso)) return false;
  if (!org.operates_holidays) {
    if (isFrenchHoliday(iso)) return false;
    if ((org.closed_dates ?? []).includes(iso)) return false;
    if (org.excludes_bridge_days && isBridgeDay(iso)) return false;
  }
  const ws = org.operating_hours_start;
  const we = org.operating_hours_end;
  if (!ws || !we) return true;
  if (ws === "00:00:00" && we === "00:00:00") return true; // round-the-clock
  const wsMin = timeToMinutes(ws);
  const weMin = timeToMinutes(we);
  const ssMin = timeToMinutes(slot.start_time);
  const seMin = timeToMinutes(slot.end_time);
  if (weMin > wsMin) {
    // Single-day window — slot must lie inside [ws, we]
    return ssMin >= wsMin && seMin <= weMin;
  }
  // Crosses midnight — slot lies inside [ws, 24h] or [0, we]
  return (ssMin >= wsMin && seMin <= 24 * 60) || (ssMin >= 0 && seMin <= weMin);
}
